import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import { useTheme, useMediaQuery } from '@mui/material';
import { TableVirtuoso } from 'react-virtuoso';
import { Edit, Trash2, Eye, Database, MoreHorizontal } from 'lucide-react';
import { theme } from '../../src/theme';

const VirtuosoTableComponents = {
  Scroller: React.forwardRef((props, ref) => (
    <TableContainer
      component={Paper}
      {...props}
      ref={ref}
      sx={{
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.boxShadow.md,
        border: `1px solid ${theme.colors.border.light}`,
        backgroundColor: theme.colors.background.primary,
        ...props.sx
      }}
    />
  )),
  Table: (props) => (
    <Table
      {...props}
      sx={{
        borderCollapse: 'separate',
        tableLayout: 'fixed',
        fontFamily: theme.typography.fontFamily.sans.join(', '),
        fontSize: theme.typography.fontSize.sm,
        ...props.sx
      }}
    />
  ),
  TableHead: React.forwardRef((props, ref) => (
    <TableHead
      {...props}
      ref={ref}
      sx={{
        backgroundColor: theme.colors.primary[50],
        ...props.sx
      }}
    />
  )),
  TableRow: (props) => (
    <TableRow
      {...props}
      sx={{
        '&:nth-of-type(even)': {
          backgroundColor: theme.colors.background.secondary,
        },
        '&:hover': {
          backgroundColor: theme.colors.primary[50],
          cursor: 'pointer',
        },
        transition: `background-color ${theme.animation.duration.fast} ${theme.animation.easing['ease-in-out']}`,
        ...props.sx
      }}
    />
  ),
  TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

const EmptyState = ({ height }) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: height - 100,
        minHeight: 200,
        padding: theme.spacing.xl,
        textAlign: 'center',
        backgroundColor: theme.colors.background.primary,
      }}
    >
      <Database
        size={64}
        style={{
          color: theme.colors.text.secondary,
          marginBottom: theme.spacing.lg,
          opacity: 0.5
        }}
      />
      <Typography
        variant="h6"
        sx={{
          color: theme.colors.text.secondary,
          fontFamily: theme.typography.fontFamily.sans.join(', '),
          fontWeight: theme.typography.fontWeight.medium,
          marginBottom: theme.spacing.sm,
        }}
      >
        {t('common.no_data')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: theme.colors.text.tertiary,
          fontFamily: theme.typography.fontFamily.sans.join(', '),
          maxWidth: 300,
        }}
      >
        {t('common.no_data_description')}
      </Typography>
    </Box>
  );
};

// Mobile Card Component for each row
const MobileCard = ({
  row,
  columns,
  onView,
  onEdit,
  onDelete,
  onRowClick,
  index,
}) => {
  return (
    <Card
      onClick={() => onRowClick?.(row, index)}
      sx={{
        margin: theme.spacing.sm,
        borderRadius: theme.borderRadius.md,
        boxShadow: theme.boxShadow.sm,
        border: `1px solid ${theme.colors.border.light}`,
        backgroundColor: theme.colors.background.primary,
        cursor: onRowClick ? 'pointer' : 'default',
      }}
    >
      <CardContent sx={{ padding: theme.spacing.md }}>
        {columns.map((column, idx) => (
          <Box key={column.dataKey} sx={{ marginBottom: idx < columns.length - 1 ? theme.spacing.sm : 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.colors.text.secondary,
                  fontWeight: theme.typography.fontWeight.medium,
                  fontSize: theme.typography.fontSize.xs,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: theme.spacing.xs,
                  display: 'block',
                }}
              >
                {column.label}
              </Typography>
              {idx === 0 && (onView || onEdit || onDelete) && (
                <Box sx={{ display: 'flex', gap: theme.spacing.xs }}>
                  {onView && (
                    <Tooltip title="Voir détails" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onView(row, index);
                        }}
                        sx={{
                          color: theme.colors.info[600],
                          padding: theme.spacing.xs,
                          '&:hover': {
                            backgroundColor: theme.colors.info[50],
                            color: theme.colors.info[700],
                          },
                        }}
                      >
                        <Eye size={14} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {onEdit && (
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(row, index);
                        }}
                        sx={{
                          color: theme.colors.warning[600],
                          padding: theme.spacing.xs,
                          '&:hover': {
                            backgroundColor: theme.colors.warning[50],
                            color: theme.colors.warning[700],
                          },
                        }}
                      >
                        <Edit size={14} />
                      </IconButton>
                    </Tooltip>
                  )}

                  {onDelete && (
                    <Tooltip title="Supprimer" arrow>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(row, index);
                        }}
                        sx={{
                          color: theme.colors.error[600],
                          padding: theme.spacing.xs,
                          '&:hover': {
                            backgroundColor: theme.colors.error[50],
                            color: theme.colors.error[700],
                          },
                        }}
                      >
                        <Trash2 size={14} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: theme.colors.text.primary,
                fontWeight: theme.typography.fontWeight.normal,
                fontSize: theme.typography.fontSize.sm,
                wordBreak: 'break-word',
              }}
            >
              {row[column.dataKey] || '-'}
            </Typography>
            {idx < columns.length - 1 && (
              <Divider sx={{ marginTop: theme.spacing.sm, opacity: 0.3 }} />
            )}
          </Box>
        ))}
      </CardContent>
    </Card>
  );
};

export default function ReactVirtualizedTable({
  data = [],
  columns = [],
  height = 400,
  width = '100%',
  showActions = true,
  onView = null,
  onEdit = null,
  onDelete = null,
  onRowClick = null,
  actionsWidth = 120,
  mobileColumnsToShow = 3, // How many columns to show in mobile view
  selectable = false, // Enable row selection
  selectedIds = [], // Array of selected row IDs
  onSelectionChange = null, // Callback when selection changes
  rowIdKey = 'id', // Key to use for row ID
}) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Selection handlers
  const handleSelectAll = (event) => {
    if (!onSelectionChange) return;

    if (event.target.checked) {
      const allIds = data.map(row => row[rowIdKey]);
      onSelectionChange(allIds);
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectRow = (rowId) => {
    if (!onSelectionChange) return;

    if (selectedIds.includes(rowId)) {
      onSelectionChange(selectedIds.filter(id => id !== rowId));
    } else {
      onSelectionChange([...selectedIds, rowId]);
    }
  };

  const isAllSelected = selectable && data.length > 0 && selectedIds.length === data.length;
  const isIndeterminate = selectable && selectedIds.length > 0 && selectedIds.length < data.length;

  // Filter columns for mobile view
  const mobileColumns = React.useMemo(() => {
    if (!isMobile) return columns;
    return columns.slice(0, Math.min(mobileColumnsToShow, columns.length));
  }, [columns, isMobile, mobileColumnsToShow]);

  // Add selection and actions columns to the columns array (desktop only)
  const enhancedColumns = React.useMemo(() => {
    if (isMobile) return mobileColumns;

    const cols = [];

    // Add checkbox column if selectable
    if (selectable) {
      cols.push({
        width: 60,
        label: '',
        dataKey: '__selection__',
        numeric: false,
        isSelection: true
      });
    }

    // Add regular columns
    cols.push(...columns);

    // Add actions column if showActions
    if (showActions) {
      cols.push({
        width: actionsWidth,
        label: 'Actions',
        dataKey: 'actions',
        numeric: false,
        isActions: true
      });
    }

    return cols;
  }, [columns, showActions, actionsWidth, isMobile, mobileColumns, selectable]);

  const renderActionButtons = React.useCallback((row, index) => {
    return (
      <div style={{ display: 'flex', gap: theme.spacing.xs, justifyContent: 'center' }}>
        {/* {onView && (
          <Tooltip title="Voir détails" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onView(row, index);
              }}
              sx={{
                color: theme.colors.info[600],
                '&:hover': {
                  backgroundColor: theme.colors.info[50],
                  color: theme.colors.info[700],
                },
                transition: `all ${theme.animation.duration.fast} ${theme.animation.easing['ease-in-out']}`,
              }}
            >
              <Eye size={16} />
            </IconButton>
          </Tooltip>
        )} */}

        {onEdit && (
          <Tooltip title="Modifier" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(row, index);
              }}
              sx={{
                color: theme.colors.warning[600],
                '&:hover': {
                  backgroundColor: theme.colors.warning[50],
                  color: theme.colors.warning[700],
                },
                transition: `all ${theme.animation.duration.fast} ${theme.animation.easing['ease-in-out']}`,
              }}
            >
              <Edit size={16} />
            </IconButton>
          </Tooltip>
        )}

        {onDelete && (
          <Tooltip title="Supprimer" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(row, index);
              }}
              sx={{
                color: theme.colors.error[600],
                '&:hover': {
                  backgroundColor: theme.colors.error[50],
                  color: theme.colors.error[700],
                },
                transition: `all ${theme.animation.duration.fast} ${theme.animation.easing['ease-in-out']}`,
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          </Tooltip>
        )}
      </div>
    );
  }, [onView, onEdit, onDelete]);

  const fixedHeaderContent = React.useCallback(() => {
    return (
      <TableRow>
        {enhancedColumns.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.numeric || false ? 'right' : column.isActions || column.isSelection ? 'center' : 'left'}
            style={{ width: column.width }}
            sx={{
              backgroundColor: theme.colors.primary[100],
              color: theme.colors.text.primary,
              fontWeight: theme.typography.fontWeight.semibold,
              fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm,
              borderBottom: `2px solid ${theme.colors.primary[200]}`,
              padding: isMobile ? theme.spacing.sm : theme.spacing.md,
              fontFamily: theme.typography.fontFamily.sans.join(', '),
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            {column.isSelection ? (
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={input => {
                  if (input) {
                    input.indeterminate = isIndeterminate;
                  }
                }}
                onChange={handleSelectAll}
                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
              />
            ) : (
              column.label
            )}
          </TableCell>
        ))}
      </TableRow>
    );
  }, [enhancedColumns, isMobile, isAllSelected, isIndeterminate, handleSelectAll]);

  const rowContent = React.useCallback((_index, row) => {
    const rowId = row[rowIdKey];
    const isSelected = selectedIds.includes(rowId);

    return (
      <React.Fragment>
        {enhancedColumns.map((column) => (
          <TableCell
            key={column.dataKey}
            onClick={
              !column.isSelection && !column.isActions && onRowClick
                ? () => onRowClick(row, _index)
                : undefined
            }
            align={column.numeric || false ? 'right' : column.isActions || column.isSelection ? 'center' : 'left'}
            sx={{
              color: theme.colors.text.primary,
              fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.normal,
              padding: isMobile ? theme.spacing.sm : theme.spacing.md,
              borderBottom: `1px solid ${theme.colors.border.light}`,
              fontFamily: theme.typography.fontFamily.sans.join(', '),
              lineHeight: theme.typography.lineHeight.normal,
              wordBreak: isMobile ? 'break-word' : 'normal',
              maxWidth: isMobile ? '150px' : 'none',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: isMobile ? 'nowrap' : 'normal',
              backgroundColor: isSelected ? theme.colors.primary[50] : 'transparent',
              cursor:
                !column.isSelection && !column.isActions && onRowClick
                  ? 'pointer'
                  : 'default',
            }}
          >
            {column.isSelection ? (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => handleSelectRow(rowId)}
                onClick={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
              />
            ) : column.isActions ? (
              renderActionButtons(row, _index)
            ) : (
              row[column.dataKey]
            )}
          </TableCell>
        ))}
      </React.Fragment>
    );
  }, [enhancedColumns, renderActionButtons, isMobile, selectedIds, rowIdKey, handleSelectRow, onRowClick]);

  // Handle empty data case
  if (!data || data.length === 0) {
    return (
      <Paper
        style={{
          height: isMobile ? 'auto' : height,
          width,
          minHeight: isMobile ? 300 : height
        }}
        sx={{
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.boxShadow.lg,
          border: `1px solid ${theme.colors.border.light}`,
          backgroundColor: theme.colors.background.primary,
          overflow: 'hidden',
        }}
      >
        {!isMobile && (
          <TableContainer>
            <Table sx={{
              borderCollapse: 'separate',
              tableLayout: 'fixed',
              fontFamily: theme.typography.fontFamily.sans.join(', '),
              fontSize: theme.typography.fontSize.sm,
            }}>
              <TableHead sx={{ backgroundColor: theme.colors.primary[50] }}>
                {fixedHeaderContent()}
              </TableHead>
            </Table>
          </TableContainer>
        )}
        <EmptyState height={isMobile ? 300 : height} />
      </Paper>
    );
  }

  // Mobile view - render cards instead of table
  if (isMobile) {
    return (
      <Paper
        style={{
          width,
          minHeight: 300,
          maxHeight: height,
          overflow: 'auto'
        }}
        sx={{
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.boxShadow.lg,
          border: `1px solid ${theme.colors.border.light}`,
          backgroundColor: theme.colors.background.secondary,
          padding: theme.spacing.xs,
        }}
      >
        {data.map((row, index) => (
          <MobileCard
            key={index}
            row={row}
            columns={mobileColumns}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            onRowClick={onRowClick}
            index={index}
          />
        ))}
      </Paper>
    );
  }

  // Desktop view - render virtualized table
  return (
    <Paper
      style={{ height, width }}
      sx={{
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.boxShadow.lg,
        border: `1px solid ${theme.colors.border.light}`,
        backgroundColor: theme.colors.background.primary,
        overflow: 'hidden',
      }}
    >
      <TableVirtuoso
        data={data}
        components={VirtuosoTableComponents}
        fixedHeaderContent={fixedHeaderContent}
        itemContent={rowContent}
        style={{
          backgroundColor: theme.colors.background.primary,
        }}
      />
    </Paper>
  );
}
